import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, MapPin, Building2, BookOpen } from "lucide-react";
import { courseSlug } from "@/lib/seo";

export default function HeroSearch() {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [courses, setCourses] = useState([]);
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [course, setCourse] = useState("");

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data.states || [])).catch(() => {});
    api.get("/courses").then((r) => setCourses((r.data.courses || []).slice(0, 40))).catch(() => {});
  }, []);

  useEffect(() => {
    setCity("");
    if (!state) { setCities([]); return; }
    api.get(`/cities?state=${state}`).then((r) => setCities(r.data.cities || [])).catch(() => {});
  }, [state]);

  const submit = () => {
    const dims = [state, city, course].filter(Boolean).length;
    if (dims === 1 && city) return navigate(`/city/${city}`);
    if (dims === 1 && state) return navigate(`/state/${state}`);
    if (dims === 1 && course) return navigate(`/course/${courseSlug(course)}`);
    const p = new URLSearchParams();
    if (state) p.set("state", state);
    if (city) p.set("city", city);
    if (course) p.set("course", course);
    const qs = p.toString();
    navigate(qs ? `/colleges?${qs}` : "/colleges");
  };

  const selCls = "w-full appearance-none rounded-xl border border-slate-200 bg-white py-3 pl-10 pr-8 text-sm text-slate-700 outline-none focus:border-[#0B2A6B] focus:ring-2 focus:ring-[#0B2A6B]/15";

  return (
    <div data-testid="hero-search" className="rounded-2xl bg-white p-3 shadow-xl ring-1 ring-slate-200/70">
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="relative">
          <MapPin className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select data-testid="hero-state-select" value={state} onChange={(e) => setState(e.target.value)} className={selCls}>
            <option value="">All States</option>
            {states.map((s) => <option key={s.slug} value={s.slug}>{s.name} ({s.colleges})</option>)}
          </select>
        </div>
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select data-testid="hero-city-select" value={city} onChange={(e) => setCity(e.target.value)} disabled={!state} className={`${selCls} disabled:bg-slate-50 disabled:text-slate-400`}>
            <option value="">{state ? "All Cities" : "Select a state first"}</option>
            {cities.map((c) => <option key={c.slug} value={c.slug}>{c.name} ({c.colleges})</option>)}
          </select>
        </div>
        <div className="relative">
          <BookOpen className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <select data-testid="hero-course-select" value={course} onChange={(e) => setCourse(e.target.value)} className={selCls}>
            <option value="">All Courses</option>
            {courses.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <button data-testid="hero-search-btn" onClick={submit} className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#0B2A6B] py-3.5 text-sm font-bold text-white hover:bg-[#081E4D] transition-colors">
        <Search className="h-4 w-4" /> Search Colleges
      </button>
    </div>
  );
}
